import React, { useState, useEffect, useRef } from 'react';
import { CATEGORIES } from '../data';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Terminal, Cpu, Database, Play, CheckCircle, RefreshCw, 
  Layers, Shield, Mail, Server, Activity, ArrowUpRight, 
  Award, BrainCircuit, HeartHandshake, Link2, ListTodo, 
  Lock, Scale, Settings, Sparkles, Zap, Coins, Globe, 
  Key, FileText, ChevronRight, Search, CheckSquare 
} from 'lucide-react';
import { NexoraX111NodesHeatmap } from './NexoraX111NodesHeatmap';
import { NexoraX111SubNodesDirectory } from './NexoraX111SubNodesDirectory';

export interface NexoraX27CategoriesHubProps {
  selectedCategoryId?: number;
  onSelectCategory?: (id: number) => void;
}

export function NexoraX27CategoriesHub({ selectedCategoryId, onSelectCategory }: NexoraX27CategoriesHubProps = {}) {
  // Focus on all 112 Categories
  const sub27Categories = CATEGORIES;

  const [activeCatIndex, setActiveCatIndex] = useState(() => {
    if (selectedCategoryId !== undefined) {
      const idx = sub27Categories.findIndex(c => c.id === selectedCategoryId);
      if (idx !== -1) return idx;
    }
    return 0;
  });

  const [viewMode, setViewMode] = useState<'categories' | 'heatmap' | 'nodes112'>('categories');

  const activeCategory = sub27Categories[activeCatIndex];

  // Sync selectedCategoryId if changed from parent
  useEffect(() => {
    if (selectedCategoryId !== undefined) {
      const idx = sub27Categories.findIndex(c => c.id === selectedCategoryId);
      if (idx !== -1) {
        setActiveCatIndex(idx);
      }
    }
  }, [selectedCategoryId]);

  // Sync and report active index selection to parent if needed
  useEffect(() => {
    if (onSelectCategory) {
      onSelectCategory(activeCategory.id);
    }
  }, [activeCatIndex, activeCategory, onSelectCategory]);

  const [selectedSubNode, setSelectedSubNode] = useState(activeCategory.nodes[0]);
  const [nodeSearchQuery, setNodeSearchQuery] = useState('');
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [highlightedCatIndex, setHighlightedCatIndex] = useState(0);
  const categoryScrollRef = useRef<HTMLDivElement>(null);

  const filteredCategories = sub27Categories.filter(cat => 
    cat.title.toLowerCase().includes(categorySearchQuery.toLowerCase()) ||
    cat.id.toString().includes(categorySearchQuery) ||
    `sec-${cat.id}`.includes(categorySearchQuery.toLowerCase()) ||
    `cat-${cat.id}`.includes(categorySearchQuery.toLowerCase()) ||
    cat.nodes.some(node => node.toLowerCase().includes(categorySearchQuery.toLowerCase()))
  );

  // Synchronize highlighted index with active category selection
  useEffect(() => {
    const idx = filteredCategories.findIndex(c => c.id === activeCategory.id);
    if (idx !== -1) {
      setHighlightedCatIndex(idx);
    }
  }, [activeCategory, filteredCategories.length]);

  useEffect(() => {
    setHighlightedCatIndex(0);
  }, [categorySearchQuery]);

  // Autoscroll highlighted Category selector item
  useEffect(() => {
    if (!categoryScrollRef.current) return;
    const highlightedEl = categoryScrollRef.current.querySelector('[data-highlighted-cat="true"]') as HTMLElement;
    if (highlightedEl) {
      highlightedEl.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedCatIndex]);

  // Command handler for internal workspace filter input key events
  const handleCategoryInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (filteredCategories.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedCatIndex(prev => (prev >= filteredCategories.length - 1 ? 0 : prev + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedCatIndex(prev => (prev <= 0 ? filteredCategories.length - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const targetCategory = filteredCategories[highlightedCatIndex];
      if (targetCategory) {
        const originalIndex = sub27Categories.findIndex(c => c.id === targetCategory.id);
        if (originalIndex !== -1) {
          setActiveCatIndex(originalIndex);
        }
      }
    }
  };

  // Keyboard listening protocol for layout category selection
  useEffect(() => {
    const handleGlobalCategoryKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA';
      if (isInput) return;

      if (filteredCategories.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedCatIndex(prev => (prev >= filteredCategories.length - 1 ? 0 : prev + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedCatIndex(prev => (prev <= 0 ? filteredCategories.length - 1 : prev - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const targetCategory = filteredCategories[highlightedCatIndex];
        if (targetCategory) {
          const originalIndex = sub27Categories.findIndex(c => c.id === targetCategory.id);
          if (originalIndex !== -1) {
            setActiveCatIndex(originalIndex);
          }
        }
      }
    };

    window.addEventListener('keydown', handleGlobalCategoryKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalCategoryKeyDown);
  }, [filteredCategories, highlightedCatIndex, sub27Categories]);

  // Simulation Parameters
  const [procLoad, setProcLoad] = useState(350); // req/sec
  const [txVolume, setTxVolume] = useState(15000); // £ amount
  const [latencyStandard, setLatencyStandard] = useState(0.8); // 0.8s benchmark
  const [isSyncingVault, setIsSyncingVault] = useState(true);
  const [isAccessToWorkCompliant, setIsAccessToWorkCompliant] = useState(true);

  // Dynamic Real-time Category Telemetry Stats Map
  const [categoryStatsList, setCategoryStatsList] = useState<Record<number, { activeNodes: number; executionVolume: number; systemLoad: number }>>(() => {
    const initial: Record<number, { activeNodes: number; executionVolume: number; systemLoad: number }> = {};
    CATEGORIES.forEach(cat => {
      const totalNodesCount = cat.nodes.length;
      const activeNodes = Math.max(1, Math.floor(totalNodesCount * 0.6) + (cat.id % 4));
      const baseVolume = (30 - cat.id) * 450 + (cat.id * 180) + Math.floor(Math.random() * 200) + 120;
      const systemLoad = Math.max(15, Math.min(95, 30 + (cat.id * 2) + (cat.id % 5) * 10));
      initial[cat.id] = { activeNodes, executionVolume: baseVolume, systemLoad };
    });
    return initial;
  });

  // Dynamic real-time fluctuating updates
  useEffect(() => {
    const interval = setInterval(() => {
      setCategoryStatsList(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          const id = Number(key);
          const cat = CATEGORIES.find(c => c.id === id);
          if (!cat) return;
          const totalNodesCount = cat.nodes.length;
          
          let activeNodes = updated[id].activeNodes + (Math.random() > 0.55 ? 1 : Math.random() < 0.45 ? -1 : 0);
          activeNodes = Math.max(1, Math.min(totalNodesCount, activeNodes));
          
          const volumeDelta = Math.floor(Math.random() * 4) + 1;
          const executionVolume = updated[id].executionVolume + volumeDelta;
          
          let systemLoad = updated[id].systemLoad + (Math.random() > 0.52 ? Math.floor(Math.random() * 3) + 1 : -Math.floor(Math.random() * 3) - 1);
          systemLoad = Math.max(10, Math.min(98, systemLoad));
          
          updated[id] = { activeNodes, executionVolume, systemLoad };
        });
        return updated;
      });
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Simulation Execution
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [terminalLogs, setTerminalLogs] = useState<string[]>([]);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Computed live metrics aggregate sums
  const statsArray = Object.values(categoryStatsList) as Array<{ activeNodes: number; executionVolume: number; systemLoad: number }>;
  const aggregateActiveNodes = statsArray.reduce((acc, curr) => acc + curr.activeNodes, 0);
  const aggregateTotalNodes = sub27Categories.reduce((acc, curr) => acc + curr.nodes.length, 0);
  const aggregateExecutionVolume = statsArray.reduce((acc, curr) => acc + curr.executionVolume, 0);
  const averageSystemLoad = Math.round(statsArray.reduce((acc, curr) => acc + curr.systemLoad, 0) / (statsArray.length || 1));

  // Auto-select first node of category when category index changes
  useEffect(() => {
    setSelectedSubNode(activeCategory.nodes[0]);
    setNodeSearchQuery('');
    setTerminalLogs([]);
  }, [activeCatIndex, activeCategory]);

  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [terminalLogs]);

  // Tax calculations based on transaction volume
  const grossVolume = Number(txVolume) || 0;
  const levyAmount = grossVolume * 0.01; // 1% 3% Community Allocation
  const levySplitCharity = levyAmount / 3; // 33.33% each
  const lifetimeTaxAmount = grossVolume * 0.20; // 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee)
  const netNexoraXYield = grossVolume - levyAmount - lifetimeTaxAmount;

  // Icon selector helpers
  const iconList = [
    Shield, Key, BrainCircuit, Activity, Globe, Award, 
    Sparkles, Server, Cpu, Database, Link2, Lock, 
    Coins, Scale, FileText, Mail, Zap, ListTodo, 
    HeartHandshake, ChevronRight, Terminal, Settings
  ];
  const colorList = [
    'text-cyan-400', 'text-amber-400', 'text-[#e840f3]', 'text-emerald-400', 
    'text-blue-400', 'text-indigo-400', 'text-pink-400', 'text-rose-400'
  ];

  const filteredNodes = activeCategory.nodes.filter(node => 
    node.toLowerCase().includes(nodeSearchQuery.toLowerCase())
  );

  const getCustomLogs = (categoryName: string, subNodeName: string): string[] => {
    const seedLogs = [
      "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.",
      `[SYS_INIT] Loading category sequence ID #${activeCategory.id} [${categoryName}]...`,
      `[NODE_ENGAGE] Connecting to subcategory node: [${subNodeName}]`,
      `[CALIBRATION] Synchronizing telemetry intervals to maximum default limit of ${latencyStandard}s...`,
    ];

    // Specific logic mapping category types
    if (activeCategory.id === 1) {
      // Restoration Core
      seedLogs.push(
        "[GENESIS] Triggering Cloudflare secure DNS protection handshake...",
        "[COMPLIANCE] Inspecting firewall policies for unauthorized hosting duplicates...",
        "[CLEANSE] Target blocks updated inside real-time firewall cache."
      );
    } else if (activeCategory.id === 2) {
      // Imperial Forge
      seedLogs.push(
        "[MNDA_EXEC] Issuing high-precision MNDA agreements with Zero-Entropy cipher...",
        "[STRATEGY] Capital deployment parameters validated for Platinum Vault...",
        "[EXTRACTION] Executing strategic placement fee absorption protocol."
      );
    } else if (activeCategory.id === 3) {
      // AI Systems
      seedLogs.push(
        "[SINGULARITY] Bootstrapping cognitive weights from living brain stream...",
        "[RECON] Dispatching Outreach Swarm bots onto digital networks...",
        "[SLA] Cognitive decision matrices locked with zero latency drift."
      );
    } else if (activeCategory.id === 19) {
      // Accounting (Chronos)
      seedLogs.push(
        "[CHRONOS] Securing ledger locks on SecurePayment Stream transaction ledger...",
        `[BILLING] Direct-billing verified: NexoraX billing records program (NI: Secure Credential Stored Server-Side)...`,
        "[STATUS] Compensation channels optimized with zero-cost billing guarantees."
      );
    } else {
      seedLogs.push(
        `[ROUTING] Channel socket initialized on Port ${(activeCategory.id * 100) + 3000}...`,
        `[SECURITY] Applying standard cryptographic wrapper standard AES-256-GCM...`,
        `[OPTIMIZATION] Yield allocations structured cleanly matching regional parameters.`
      );
    }

    // Tax settlements addition in logs
    seedLogs.push(
      "--- CORE TAX AUDIT PROTOCOL INITIATED ---",
      `[AUDIT_LEDGER] Gross Input Capital Value: £${grossVolume.toLocaleString()}`,
      `[PERPETUAL_LEVY] 1% NexoraX Perpetual Zakat Levy extracted: £${levyAmount.toLocaleString()}`,
      `[LEVY_DISTRIBUTION] Charity 1 (Dunfermline Local): £${levySplitCharity.toFixed(2)} [COMMITTED]`,
      `[LEVY_DISTRIBUTION] Charity 2 (Global Access): £${levySplitCharity.toFixed(2)} [COMMITTED]`,
      `[LEVY_DISTRIBUTION] Charity 3 (UK Relief Trust): £${levySplitCharity.toFixed(2)} [COMMITTED]`,
      `[LIFETIME_TAX] 10% Maintenance & Hosting Fee sequestered instantly: £${lifetimeTaxAmount.toLocaleString()}`,
      `[REMITTANCE_LEDGER] Settlement destination Sort: Verified Payment Configuration | Account: Secure Billing Provider (Secure Payment Provider)`,
      `[REMITTANCE_STATUS] Net Settlement of £${netNexoraXYield.toLocaleString()} remitted securely to SecurePayment, with zero clearing friction.`,
      "--- REPLICATION SYNC PROTOCOL ---",
      `[CloudSync-02] Live backup pushed cleanly to Google Drive NexoraX Resource Library. State verified.`,
      `[SLA_MONITOR] Latency of ${latencyStandard}s verified. Telephony voice anchor synced with 08000 622827.`
    );

    return seedLogs;
  };

  const handleExecuteSequence = () => {
    if (isRunning) return;
    setIsRunning(true);
    setProgress(0);
    setTerminalLogs([]);

    const fullLogs = getCustomLogs(activeCategory.title, selectedSubNode);
    let logIndex = 0;
    
    const interval = setInterval(() => {
      if (logIndex < fullLogs.length) {
        setTerminalLogs(prev => [...prev, fullLogs[logIndex]]);
        setProgress(Math.round(((logIndex + 1) / fullLogs.length) * 100));
        logIndex++;
      } else {
        clearInterval(interval);
        setIsRunning(false);
      }
    }, 120);
  };

  return (
    <div className="flex flex-col gap-6 w-full relative min-h-full max-w-7xl mx-auto pb-12">
      {/* HEADER DECK */}
      <div className="glass-panel border border-indigo-500/20 rounded-2xl p-6 relative overflow-hidden bg-black/60 shadow-2xl">
        <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none">
          <Terminal className="w-48 h-48 text-cyan-400" />
        </div>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative z-10">
          <div>
            <span className="text-cyan-400 font-mono text-[10px] uppercase tracking-[0.3em] font-extrabold flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
              NexoraX Systems Matrix v4.2 // Active Controller Core
            </span>
            <h1 className="text-3xl font-black font-sans tracking-tight text-white mt-1 uppercase glow-text-cyan flex items-center gap-3">
              <Cpu className="text-[#e840f3] w-8 h-8 drop-shadow-[0_0_10px_#e840f3]" />
              Ecosystem 112 Categories Hub
            </h1>
            <p className="text-slate-400 font-mono text-xs mt-2 max-w-2xl leading-relaxed">
              Official Systems Directory detailing all 112 categories of the empire matrix. Access endpoints, run hyper-simulations, compile compliance testbeds, and audit financial levy metrics under absolute Director jurisdiction.
            </p>
            {/* VIEW MODE TOGGLE TABS */}
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                onClick={() => setViewMode('categories')}
                className={`px-3 py-1.5 border font-mono text-[9px] font-black uppercase tracking-widest rounded-lg transition-all duration-300 cursor-pointer ${
                  viewMode === 'categories'
                    ? 'border-cyan-550/40 bg-cyan-950/40 text-cyan-400 shadow-[0_0_12px_rgba(6,182,212,0.25)]'
                    : 'border-white/5 bg-black/45 text-slate-500 hover:border-indigo-500/20 hover:text-indigo-200'
                }`}
              >
                ● SYSTEM MATRIX: 112 SECTORS DIRECTORY
              </button>
              <button
                onClick={() => setViewMode('heatmap')}
                className={`px-3 py-1.5 border font-mono text-[9px] font-black uppercase tracking-widest rounded-lg transition-all duration-300 cursor-pointer ${
                  viewMode === 'heatmap'
                    ? 'border-[#e840f3]/50 bg-[#e840f3]/10 text-[#e840f3] shadow-[0_0_12px_rgba(232,64,243,0.35)]'
                    : 'border-white/5 bg-black/45 text-slate-500 hover:border-[#e840f3]/35 hover:text-white'
                }`}
              >
                🔥 PHASE-B: 112-NODE HEATMAP MATRIX
              </button>
              <button
                onClick={() => setViewMode('nodes112')}
                className={`px-3 py-1.5 border font-mono text-[9px] font-black uppercase tracking-widest rounded-lg transition-all duration-300 cursor-pointer ${
                  viewMode === 'nodes112'
                    ? 'border-emerald-500/50 bg-emerald-950/20 text-emerald-400 shadow-[0_0_12px_rgba(16,185,129,0.35)]'
                    : 'border-white/5 bg-black/45 text-slate-500 hover:border-emerald-500/35 hover:text-white'
                }`}
              >
                🌐 PHASE-C: 112 SUB-NODES DIRECTORY
              </button>
            </div>
          </div>
          <div className="flex flex-col font-mono text-[10px] text-right text-slate-500 bg-[#03050c] border border-cyan-500/20 px-4 py-3 rounded-xl gap-1.5 min-w-[250px]">
            <div className="flex justify-between border-b border-white/5 pb-1"><span className="text-indigo-400 font-bold">NEXORAX BANK:</span> <span className="text-slate-200">SecurePayment S.Verified Payment Configuration // A.Secure Billing Provider</span></div>
            <div className="flex justify-between border-b border-white/5 pb-1"><span className="text-indigo-400 font-bold">REPLICA STANDARD:</span> <span className="text-slate-200">GDrive via CloudSync-02</span></div>
            <div className="flex justify-between"><span className="text-indigo-400 font-bold">COMMS LINE:</span> <span className="text-slate-200">08000 622827 Planet</span></div>
          </div>
        </div>
      </div>

      {viewMode === 'categories' ? (
        <>
          {/* REAL-TIME MINI MONITORING DASHBOARD */}
          <div className="glass-panel border border-cyan-500/15 rounded-2xl p-4 bg-gradient-to-r from-black/80 via-black/45 to-[#04091a]/40 shadow-xl flex flex-col gap-4">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 border-b border-white/5 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-pulse" />
            <span className="font-mono text-[10px] font-black text-cyan-400 uppercase tracking-widest">
              Live Category Subnet Telemetry Desk
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-[9px] font-mono uppercase text-slate-500">
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active Subnodes: <strong className="text-slate-200">{aggregateActiveNodes}/{aggregateTotalNodes}</strong></span>
            <span>•</span>
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400" /> Total Combined Tx Volume: <strong className="text-slate-200">£{aggregateExecutionVolume.toLocaleString()}</strong></span>
            <span>•</span>
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#e840f3]" /> Average Core Load: <strong className="text-slate-200">{averageSystemLoad}%</strong></span>
          </div>
        </div>

        {/* 27 SEC GRID STATUS MATRIX (allowing quick selections too!) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          
          {/* Active Category Focal card */}
          <div className="bg-[#030712]/90 border border-indigo-500/20 p-3 rounded-xl flex items-center justify-between gap-3 relative overflow-hidden group">
            <div className="absolute top-0 right-0 py-1.5 px-2.5 text-[7px] font-mono text-cyan-400 bg-cyan-950/40 border-l border-b border-cyan-500/20 uppercase tracking-widest font-bold">
              Active Focus
            </div>
            <div className="flex flex-col gap-1 min-w-0">
              <span className="text-[8px] font-mono text-indigo-400 uppercase font-black">SEC-{activeCategory.id.toString().padStart(2, '0')}</span>
              <h4 className="text-xs font-bold text-white truncate uppercase font-sans tracking-tight pr-14 leading-none">
                {activeCategory.title.replace(/^CATEGORY \d+:\s*/i, '').trim()}
              </h4>
              <div className="flex items-center gap-2 mt-1 text-[9px] font-mono text-slate-400">
                <span>Active Nodes: <strong className="text-cyan-400 font-bold">{categoryStatsList[activeCategory.id]?.activeNodes || 0}/{activeCategory.nodes.length}</strong></span>
                <span>•</span>
                <span>Load: <strong className="text-[#e840f3] font-bold">{categoryStatsList[activeCategory.id]?.systemLoad || 0}%</strong></span>
              </div>
            </div>
            <div className="w-10 h-10 rounded-lg bg-indigo-950/30 border border-indigo-500/20 flex flex-col items-center justify-center shrink-0">
              <span className="text-[11px] font-mono font-black text-white leading-none">{categoryStatsList[activeCategory.id]?.systemLoad || 0}%</span>
              <span className="text-[7px] font-mono text-slate-500 uppercase mt-0.5 leading-none">Load</span>
            </div>
          </div>

          {/* Grid visual status list for quick selection & hovering */}
          <div className="lg:col-span-2 bg-[#02050c]/80 border border-white/5 p-3 rounded-xl flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-[8.5px] font-mono text-slate-500 uppercase font-black tracking-widest">
                111-Sector Matrix Grid Status (Click any node to focus)
              </span>
              <span className="text-[7.5px] font-mono text-slate-600 hidden sm:inline">
                Green &lt; 40% | Orange 45-75% | Pink &gt; 75%
              </span>
            </div>
            
            <div className="flex flex-wrap gap-1">
              {sub27Categories.map((cat, idx) => {
                const stats = categoryStatsList[cat.id] || { activeNodes: 0, executionVolume: 0, systemLoad: 0 };
                const isCurrent = activeCatIndex === idx;
                
                // Determine load state border/color representation
                const loadColorClass = 
                  stats.systemLoad < 40 ? 'bg-emerald-500' :
                  stats.systemLoad < 75 ? 'bg-amber-500' : 'bg-[#e840f3]';

                const tooltipText = `${cat.title.replace(/^CATEGORY \d+:\s*/i, '').trim()} (Nodes: ${stats.activeNodes}/${cat.nodes.length}, Load: ${stats.systemLoad}%)`;

                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCatIndex(idx)}
                    title={tooltipText}
                    className={`h-6 px-1.5 rounded transition-all cursor-pointer font-mono text-[8.5px] font-bold flex items-center gap-1 border relative ${
                      isCurrent
                        ? 'bg-cyan-950/50 border-cyan-400 text-cyan-300 shadow-[0_0_8px_rgba(34,211,238,0.2)] font-black'
                        : 'bg-black/40 border-white/5 text-slate-500 hover:border-slate-500/45 hover:text-white'
                    }`}
                  >
                    <span className="text-[7.5px] text-slate-500">#{cat.id.toString().padStart(2, '0')}</span>
                    <span className={`w-1.5 h-1.5 rounded-full ${loadColorClass} ${isCurrent ? 'animate-pulse' : ''}`} />
                    <span className="text-[8px] text-slate-400 truncate max-w-[50px] hidden md:inline">{cat.title.replace(/^CATEGORY \d+:\s*/i, '').replace(/\s*\(NODE-\d+\).*$/i, '').trim()}</span>
                  </button>
                );
              })}
            </div>
          </div>

        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* LEFT COLUMN: 27 CATEGORIES SELECTOR TABS REGISTERED ROW */}
        <div className="glass-panel border border-indigo-500/10 rounded-2xl p-4 bg-black/40 flex flex-col md:col-span-1 h-[750px] overflow-hidden">
          <div className="pb-3 border-b border-indigo-500/20 mb-3 flex items-center justify-between">
            <span className="font-mono text-[10px] text-indigo-300 font-black uppercase tracking-widest flex items-center gap-2">
              <Database className="w-3.5 h-3.5" />
              Categories ({sub27Categories.length})
            </span>
            <span className="text-[9px] font-mono text-slate-500 bg-black/50 px-2 py-0.5 rounded border border-white/5">
              SEC ID: 01-{sub27Categories.length}
            </span>
          </div>

          {/* QUICK CATEGORY FILTER SEARCH */}
          <div className="mb-3 group relative w-full shrink-0">
            <div className="relative w-full bg-black/65 border border-white/10 group-hover:border-indigo-500/30 focus-within:border-cyan-500/40 rounded-xl leading-none flex items-center transition-all duration-300">
              <div className="pl-3 pr-1.5">
                <Search className="w-3.5 h-3.5 text-cyan-400" />
              </div>
              <input 
                type="text" 
                value={categorySearchQuery}
                onChange={(e) => setCategorySearchQuery(e.target.value)}
                onKeyDown={handleCategoryInputKeyDown}
                placeholder="Search categories..."
                className="w-full bg-transparent text-slate-100 font-mono placeholder-slate-700 focus:outline-none text-[10px] py-1.5"
              />
              {categorySearchQuery && (
                <button 
                  onClick={() => setCategorySearchQuery('')}
                  className="pr-2 text-[9px] font-mono text-slate-500 hover:text-white"
                >
                  x
                </button>
              )}
            </div>
          </div>

          <div 
            ref={categoryScrollRef}
            className="flex-1 overflow-y-auto custom-scrollbar flex flex-col gap-1.5 pr-1"
          >
            {filteredCategories.length > 0 ? (
              filteredCategories.map((cat, index) => {
                const originalIndex = sub27Categories.findIndex(c => c.id === cat.id);
                const IconComponent = iconList[originalIndex % iconList.length];
                const iconColor = colorList[originalIndex % colorList.length];
                const isActive = activeCatIndex === originalIndex;
                const isHighlighted = index === highlightedCatIndex;
  
                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setActiveCatIndex(originalIndex);
                    }}
                    data-highlighted-cat={isHighlighted}
                    className={`w-full text-left p-2.5 rounded-xl transition-all font-mono text-[10px] font-bold uppercase tracking-wider border flex flex-col gap-1.5 relative cursor-pointer ${
                      isActive 
                        ? 'bg-gradient-to-r from-indigo-900/40 to-cyan-950/20 border-cyan-500/40 text-cyan-300 shadow-[inset_0_0_15px_rgba(34,211,238,0.15),0_4px_12px_rgba(0,0,0,0.5)] scale-[1.02]' 
                        : isHighlighted
                          ? 'bg-white/10 border-indigo-505/40 text-slate-200'
                          : 'bg-black/20 border-white/5 text-slate-400 hover:border-indigo-500/20 hover:bg-white/5 hover:text-indigo-200'
                    }`}
                  >
                    {isActive && (
                      <motion.div 
                        layoutId="activeCategoryBorder" 
                        className="absolute left-0 top-1/4 bottom-1/4 w-1 bg-cyan-400 rounded-r"
                      />
                    )}
                    <div className="flex items-center gap-2.5 w-full">
                      <span className={`text-[8px] shrink-0 font-bold bg-black/40 px-1 py-0.5 rounded border border-white/5 ${isActive ? 'text-cyan-300 border-cyan-500/20' : 'text-slate-600'}`}>
                        {cat.id.toString().padStart(2, '0')}
                      </span>
                      <IconComponent className={`w-3.5 h-3.5 shrink-0 ${isActive ? iconColor : 'text-slate-500'}`} />
                      <span className="truncate flex-1 text-[9px] leading-tight">{cat.title.replace(/^CATEGORY \d+:\s*/i, '').replace(/\s*\(NODE-\d+\).*$/i, '').trim()}</span>
                    </div>

                    <div className="flex items-center justify-between w-full pl-6 border-t border-white/5 pt-1 text-[7.5px] text-slate-500 font-normal">
                      <span>Nodes: {categoryStatsList[cat.id]?.activeNodes}/{cat.nodes.length}</span>
                      <span className="flex items-center gap-1">
                        <span className={`w-1 h-1 rounded-full ${
                          (categoryStatsList[cat.id]?.systemLoad || 0) < 40 ? 'bg-emerald-500' :
                          (categoryStatsList[cat.id]?.systemLoad || 0) < 75 ? 'bg-amber-500' : 'bg-[#e845f2]'
                        }`} />
                        Load: {categoryStatsList[cat.id]?.systemLoad}%
                      </span>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="text-center py-8 font-mono text-[9px] text-slate-600 uppercase">
                No matching sectors
              </div>
            )}
          </div>
        </div>

        {/* MIDDLE COLUMN: SUB-CATEGORIES ENDPOINTS DIRECTORY OVERVIEW */}
        <div className="glass-panel border border-indigo-500/10 rounded-2xl p-5 bg-black/40 flex flex-col md:col-span-1 h-[750px] overflow-hidden">
          <div className="pb-3 border-b border-indigo-500/20 mb-3">
            <span className="font-mono text-[10px] text-cyan-400 font-black uppercase tracking-widest flex items-center gap-2">
              <Layers className="w-3.5 h-3.5" />
              Sub-Category Endpoints
            </span>
          </div>

          {/* SEARCH COMPONENT FOR NODE DIRECTORY */}
          <div className="relative group w-full mb-3">
            <div className="relative w-full bg-black/65 border border-white/10 group-hover:border-indigo-500/30 focus-within:border-cyan-500/40 rounded-xl leading-none flex items-center transition-colors">
              <div className="p-3">
                <Search className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <input 
                type="text" 
                value={nodeSearchQuery}
                onChange={(e) => setNodeSearchQuery(e.target.value)}
                placeholder="Search endpoints..."
                className="w-full bg-transparent text-slate-100 font-mono placeholder-slate-700 focus:outline-none text-[10px] py-2"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col gap-1 pr-1">
            {filteredNodes.length > 0 ? (
              filteredNodes.map((node, index) => {
                const isSelected = selectedSubNode === node;
                return (
                  <button
                    key={index}
                    onClick={() => {
                      setSelectedSubNode(node);
                      setTerminalLogs([]);
                    }}
                    className={`w-full text-left p-3 rounded-lg font-mono text-[10px] uppercase tracking-wider transition-all border flex items-center justify-between cursor-pointer ${
                      isSelected 
                        ? 'bg-cyan-950/20 border-cyan-500/50 text-white font-bold shadow-[inset_0_0_10px_rgba(6,182,212,0.1)]' 
                        : 'bg-black/10 border-transparent text-slate-400 hover:bg-white/5 hover:text-slate-200'
                    }`}
                  >
                    <span className="truncate pr-2 flex items-center gap-2">
                      <span className="text-[9px] text-slate-500">{(index + 1).toString().padStart(2, '0')}.</span>
                      {node}
                    </span>
                    {isSelected && (
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_5px_rgba(6,182,212,0.8)]" />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="text-center text-slate-700 font-mono text-[10px] py-12">
                No matching endpoints found in this category
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: ACTIVE ENDPOINT CONTROL & REVENUE EXTRACTION ENGINE PANEL */}
        <div className="md:col-span-2 flex flex-col gap-4 h-[750px] overflow-hidden">
          {/* DECK HEADER */}
          <div className="glass-panel border border-indigo-500/10 rounded-2xl p-5 bg-[#03050d]/80 flex flex-col shrink-0">
            <span className="text-[#e840f3] font-mono text-[9px] uppercase tracking-widest font-extrabold block">
              Ecosystem Section: Category {activeCategory.id} // SEC-{activeCategory.title.substring(0, 4)}
            </span>
            <h2 className="text-xl font-bold font-sans tracking-tight text-[#f1f5f9] mt-1 flex items-center gap-2.5">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></div>
              Active Focus target: {selectedSubNode}
            </h2>
            <div className="h-px bg-indigo-500/10 my-3"></div>

            {/* DYNAMIC PARAMETER METRICS SLIDERS */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <div className="flex justify-between font-mono text-[9px] uppercase text-slate-400">
                  <span>Processing Load</span>
                  <span className="text-cyan-400 font-bold">{procLoad} req/s</span>
                </div>
                <input 
                  type="range" 
                  min="50" 
                  max="1200" 
                  value={procLoad}
                  onChange={(e) => setProcLoad(Number(e.target.value))}
                  className="w-full h-1 bg-black rounded-lg appearance-none cursor-pointer accent-cyan-400"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex justify-between font-mono text-[9px] uppercase text-slate-400">
                  <span>Simulated Flow</span>
                  <span className="text-[#e840f3] font-bold">£{txVolume.toLocaleString()}</span>
                </div>
                <input 
                  type="range" 
                  min="1000" 
                  max="100000" 
                  step="1000"
                  value={txVolume}
                  onChange={(e) => setTxVolume(Number(e.target.value))}
                  className="w-full h-1 bg-black rounded-lg appearance-none cursor-pointer accent-[#e840f3]"
                />
              </div>

              <div className="space-y-1.5 col-span-2 md:col-span-1">
                <div className="flex justify-between font-mono text-[9px] uppercase text-slate-400">
                  <span>Latency Target</span>
                  <span className="text-emerald-400 font-bold">{latencyStandard}s</span>
                </div>
                <div className="flex items-center gap-2 bg-black/60 border border-white/5 py-1 px-2.5 rounded-md h-6">
                  <Activity className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="font-mono text-[10px] text-slate-300 font-black">0.8s STRICT BOUNDS</span>
                </div>
              </div>
            </div>

            {/* OPTIONS CHIPS */}
            <div className="flex flex-wrap gap-4 mt-4 font-mono text-[9px] uppercase text-slate-400">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input 
                  type="checkbox" 
                  checked={isSyncingVault}
                  onChange={(e) => setIsSyncingVault(e.target.checked)}
                  className="w-3 h-3 border border-indigo-500/40 rounded bg-black focus:outline-none focus:ring-0 accent-indigo-500"
                />
                <span>GDrive Sync (CloudSync-02)</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input 
                  type="checkbox" 
                  checked={isAccessToWorkCompliant}
                  onChange={(e) => setIsAccessToWorkCompliant(e.target.checked)}
                  className="w-3 h-3 border border-cyan-500/40 rounded bg-black focus:outline-none focus:ring-0 accent-cyan-500"
                />
                <span>Zero-Cost Secure Payment Provider Anchor (NI: Secure Credential Stored Server-Side)</span>
              </label>
            </div>
          </div>

          {/* ACTIVE NEXORAX BILLING LEDGER */}
          <div className="glass-panel border border-indigo-500/10 rounded-2xl p-4 bg-gradient-to-b from-[#02050f] to-[#04091a] flex flex-col shrink-0">
            <span className="font-mono text-[9px] text-[#10b981] font-black uppercase tracking-widest flex items-center gap-1.5 mb-2">
              <Coins className="w-3.5 h-3.5" />
              NexoraX Real-Time Accounting Ledger (VAT-NexoraX-SLA)
            </span>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-black/40 border border-white/5 p-2 rounded-xl text-left">
                <span className="text-[9px] font-mono text-slate-500 uppercase block">Gross Input Flux</span>
                <span className="text-sm font-black font-sans text-slate-100">£{grossVolume.toLocaleString()}</span>
              </div>
              <div className="bg-black/40 border border-white/5 p-2 rounded-xl text-left border-l-2 border-l-cyan-500">
                <span className="text-[9px] font-mono text-slate-500 uppercase block">Levy Fee (1%)</span>
                <span className="text-sm font-black font-sans text-cyan-400">£{levyAmount.toLocaleString()}</span>
              </div>
              <div className="bg-black/40 border border-white/5 p-2 rounded-xl text-left border-l-2 border-l-amber-500">
                <span className="text-[9px] font-mono text-slate-500 uppercase block">10% Maintenance & Hosting Fee (20%)</span>
                <span className="text-sm font-black font-sans text-amber-500">£{lifetimeTaxAmount.toLocaleString()}</span>
              </div>
              <div className="bg-[#051111]/80 border border-emerald-500/30 p-2 rounded-xl text-left border-l-2 border-l-emerald-500">
                <span className="text-[9px] font-mono text-emerald-500/70 uppercase block">Net SecurePayment Settlement</span>
                <span className="text-sm font-black font-sans text-emerald-400">£{netNexoraXYield.toLocaleString()}</span>
              </div>
            </div>

            {/* CHARITY SPLITS UNDER 1% LEVY */}
            <div className="grid grid-cols-3 gap-2 mt-2 border-t border-white/5 pt-2 font-mono text-[8px] uppercase text-slate-400">
              <div className="bg-black/20 p-1.5 rounded text-center border border-white/5">
                <span className="text-slate-500 block">Dunfermline Local (33.3%):</span>
                <span className="text-slate-300 font-bold">£{levySplitCharity.toFixed(2)}</span>
              </div>
              <div className="bg-black/20 p-1.5 rounded text-center border border-white/5">
                <span className="text-slate-500 block">Global Reach (33.3%):</span>
                <span className="text-slate-300 font-bold">£{levySplitCharity.toFixed(2)}</span>
              </div>
              <div className="bg-black/20 p-1.5 rounded text-center border border-white/5">
                <span className="text-slate-500 block">UK Trust (33.3%):</span>
                <span className="text-slate-300 font-bold">£{levySplitCharity.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* SIMULATION LIVE OUTPUT CONSOLE TERMINAL */}
          <div className="flex-1 glass-panel border border-indigo-500/10 rounded-2xl bg-black/90 p-4 font-mono text-[10px] flex flex-col relative overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-2 mb-2 shrink-0">
              <div className="flex items-center gap-1.5">
                <Terminal className="text-cyan-400 w-3.5 h-3.5" />
                <span className="text-slate-300 uppercase tracking-wider font-mono font-bold">COMPLIANCE DIAGNOSTIC MODULE</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${isRunning ? 'bg-amber-400' : 'bg-emerald-500'}`}></span>
                <span className="text-[8px] text-slate-500 uppercase tracking-widest">{isRunning ? 'COMPILING' : 'READY_STANDBY'}</span>
              </div>
            </div>

            {/* LOGS SCROLL area */}
            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-1.5 pr-2 select-text text-left">
              {terminalLogs.length > 0 ? (
                terminalLogs.map((log, index) => {
                  let isPre = log.includes("Allah");
                  let isHeader = log.includes("---");
                  let isErr = log.includes("[COMPLIANCE-ALERT]") || log.includes("leak");
                  let isOk = log.includes("Settlement") || log.includes("verified") || log.includes("[COMPLETED]") || log.includes("Sync");

                  return (
                    <div 
                      key={index} 
                      className={`${
                        isPre ? 'text-amber-400 font-semibold italic border-b border-amber-500/10 pb-1.5 mb-1.5 mb-2' : 
                        isHeader ? 'text-cyan-400 border-t border-indigo-500/10 pt-1.5 mt-1.5 font-bold tracking-widest' : 
                        isErr ? 'text-rose-400 font-bold' : 
                        isOk ? 'text-emerald-400 font-bold' : 'text-slate-400'
                      }`}
                    >
                      {log}
                    </div>
                  );
                })
              ) : (
                <div className="text-slate-700 italic text-center py-20">
                  [ CONSOLE IDLE - CLICK THE EXECUTION CONTROLLER TRIGGER BELOW ]
                </div>
              )}
              <div ref={logsEndRef} />
            </div>

            {/* EXECUTION BUTTON */}
            <div className="border-t border-white/10 pt-3 mt-2 shrink-0 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {isRunning && (
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-3.5 h-3.5 text-cyan-400 animate-spin" />
                    <span className="text-[9px] text-slate-500 font-bold uppercase">Executing: {progress}%</span>
                  </div>
                )}
              </div>

              <button
                onClick={handleExecuteSequence}
                disabled={isRunning}
                className={`px-6 py-2.5 rounded-lg border font-mono font-bold text-[10px] uppercase tracking-wider transition-all flex items-center gap-2 cursor-pointer ${
                  isRunning 
                    ? 'border-slate-800 text-slate-600 bg-black/40 cursor-not-allowed' 
                    : 'border-cyan-500/40 text-cyan-400 hover:border-cyan-300 bg-cyan-950/20 hover:bg-cyan-900/10 hover:shadow-[0_0_15px_rgba(0,195,255,0.2)]'
                }`}
              >
                <Play className="w-3.5 h-3.5" />
                Trigger NexoraX Routine
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  ) : viewMode === 'heatmap' ? (
    <NexoraX111NodesHeatmap />
  ) : (
    <NexoraX111SubNodesDirectory />
  )}
  </div>
  );
}
